import React, { useState, useEffect } from 'react'
import { getUserInfoSync, storeAuthData, getAccessToken, getRefreshToken } from '../../config/api'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import Swal from 'sweetalert2'

const Profile = () => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(false)
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  })
  const isFirstLogin = user?.first_login === true

  useEffect(() => {
    // Get logged-in user info
    const userInfo = getUserInfoSync()
    if (userInfo) {
      setUser(userInfo)
      // If first login, automatically show change password tab
      if (userInfo.first_login === true) {
        // Wait for DOM to be ready, then trigger the change password tab
        setTimeout(() => {
          const changePasswordTab = document.querySelector('[data-bs-target="#profile-change-password"]')
          if (changePasswordTab) {
            changePasswordTab.click()
          }
        }, 100)
      }
    }
  }, [])

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    })
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()

    // Validation
    if (!passwordData.old_password || !passwordData.new_password || !passwordData.confirm_password) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please fill in all fields'
      })
      return
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'New password and confirm password do not match'
      })
      return
    }

    if (passwordData.new_password.length < 8) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'New password must be at least 8 characters long'
      })
      return
    }

    setLoading(true)

    try {
      const response = await apiClient.post(API_ENDPOINTS.CHANGE_PASSWORD, {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password
      })

      // If first login, update user info
      if (isFirstLogin && user) {
        // Check if response includes updated user info
        let updatedUser
        if (response.data?.user) {
          updatedUser = response.data.user
        } else {
          // Fallback: update locally (backend should update first_login)
          updatedUser = { ...user, first_login: false }
        }

        // Get tokens from api config
        const accessToken = getAccessToken()
        const refreshToken = getRefreshToken()

        if (accessToken && refreshToken) {
          storeAuthData(accessToken, refreshToken, updatedUser)
          setUser(updatedUser)
        }
      }

      // Success
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: isFirstLogin
          ? 'Password changed successfully! You can now access all features. The page will reload shortly.'
          : 'Password changed successfully!',
        timer: isFirstLogin ? 2000 : 3000,
        showConfirmButton: false
      }).then(() => {
        // Reload page after first login password change to update all components
        if (isFirstLogin) {
          window.location.reload()
        }
      })

      // Reset form
      setPasswordData({
        old_password: '',
        new_password: '',
        confirm_password: ''
      })

    } catch (error) {
      // Error handling
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.detail ||
        'Failed to change password. Please check your current password and try again.'

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage
      })
    } finally {
      setLoading(false)
    }
  }

  // Format user role for display
  const formatRole = (role) => {
    if (!role) return 'User'
    return role.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ')
  }

  // Get user display name
  const getUserDisplayName = () => {
    if (user?.full_name) return user.full_name
    if (user?.first_name && user?.last_name) return `${user.first_name} ${user.last_name}`
    if (user?.username) return user.username
    if (user?.email) return user.email.split('@')[0]
    return 'User'
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Profile</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#">Home</a></li>
            <li className="breadcrumb-item active">Profile</li>
          </ol>
        </nav>
      </div>
      {isFirstLogin && (
        <div className="alert alert-warning alert-dismissible fade show" role="alert" style={{ marginBottom: '20px' }}>
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>First Login Required:</strong> Please change your password to continue. You will not be able to access other features until you change your password.
          <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        </div>
      )}
      <section className="section profile">
        <div className="row">
          <div className="col-xl-4">
            <div className="card">
              <div className="card-body profile-card pt-4 d-flex flex-column align-items-center">
                <img src="/assets/img/profile-img.jpg" alt="Profile" className="rounded-circle" />
                <h2>{user ? getUserDisplayName() : 'Loading...'}</h2>
                <h3>{user ? formatRole(user.role) : 'User'}</h3>
                <div className="social-links mt-2">
                  <a href="#" className="twitter"><i className="bi bi-twitter"></i></a>
                  <a href="#" className="facebook"><i className="bi bi-facebook"></i></a>
                  <a href="#" className="instagram"><i className="bi bi-instagram"></i></a>
                  <a href="#" className="linkedin"><i className="bi bi-linkedin"></i></a>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-8">
            <div className="card">
              <div className="card-body pt-3">
                <ul className="nav nav-tabs nav-tabs-bordered">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${!isFirstLogin ? 'active' : ''}`}
                      data-bs-toggle="tab"
                      data-bs-target="#profile-overview"
                      disabled={isFirstLogin}
                      style={isFirstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
                    >
                      Overview
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${isFirstLogin ? 'active' : ''}`}
                      data-bs-toggle="tab"
                      data-bs-target="#profile-change-password"
                    >
                      Change Password
                    </button>
                  </li>
                </ul>
                <div className="tab-content pt-2">
                  <div className={`tab-pane fade ${!isFirstLogin ? 'show active' : ''} profile-overview`} id="profile-overview">
                    <h5 className="card-title">About</h5>
                    <p className="small fst-italic">Profile information and details</p>

                    <h5 className="card-title">Profile Details</h5>

                    <div className="row">
                      <div className="col-lg-3 col-md-4 label ">Full Name</div>
                      <div className="col-lg-9 col-md-8">{user ? getUserDisplayName() : 'Loading...'}</div>
                    </div>

                    {user?.username && (
                      <div className="row">
                        <div className="col-lg-3 col-md-4 label">Username</div>
                        <div className="col-lg-9 col-md-8">{user.username}</div>
                      </div>
                    )}

                    <div className="row">
                      <div className="col-lg-3 col-md-4 label">Role</div>
                      <div className="col-lg-9 col-md-8">{user ? formatRole(user.role) : 'Loading...'}</div>
                    </div>

                    {user?.phone_number && (
                      <div className="row">
                        <div className="col-lg-3 col-md-4 label">Phone</div>
                        <div className="col-lg-9 col-md-8">{user.phone_number}</div>
                      </div>
                    )}

                    <div className="row">
                      <div className="col-lg-3 col-md-4 label">Email</div>
                      <div className="col-lg-9 col-md-8">{user?.email || 'N/A'}</div>
                    </div>

                    {user?.company && (
                      <>
                        <h5 className="card-title mt-4">Company Details</h5>
                        <div className="row">
                          <div className="col-lg-3 col-md-4 label text-primary">Company Name</div>
                          <div className="col-lg-9 col-md-8">{user.company.name}</div>
                        </div>
                        <div className="row">
                          <div className="col-lg-3 col-md-4 label">Address</div>
                          <div className="col-lg-9 col-md-8">{user.company.address}</div>
                        </div>
                        <div className="row">
                          <div className="col-lg-3 col-md-4 label">Company Phone</div>
                          <div className="col-lg-9 col-md-8">{user.company.phone}</div>
                        </div>
                        <div className="row">
                          <div className="col-lg-3 col-md-4 label">Company Email</div>
                          <div className="col-lg-9 col-md-8">{user.company.email}</div>
                        </div>
                      </>
                    )}

                  </div>

                  <div className={`tab-pane fade ${isFirstLogin ? 'show active' : ''} pt-3`} id="profile-change-password">
                    <form onSubmit={handlePasswordSubmit}>
                      <div className="row mb-3">
                        <label htmlFor="old_password" className="col-md-4 col-lg-3 col-form-label">Current Password</label>
                        <div className="col-md-8 col-lg-9">
                          <input
                            name="old_password"
                            type="password"
                            className="form-control"
                            id="old_password"
                            value={passwordData.old_password}
                            onChange={handlePasswordChange}
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="row mb-3">
                        <label htmlFor="new_password" className="col-md-4 col-lg-3 col-form-label">New Password</label>
                        <div className="col-md-8 col-lg-9">
                          <input
                            name="new_password"
                            type="password"
                            className="form-control"
                            id="new_password"
                            value={passwordData.new_password}
                            onChange={handlePasswordChange}
                            required
                            minLength={8}
                            disabled={loading}
                          />
                          <small className="form-text text-muted">Password must be at least 8 characters long</small>
                        </div>
                      </div>

                      <div className="row mb-3">
                        <label htmlFor="confirm_password" className="col-md-4 col-lg-3 col-form-label">Re-enter New Password</label>
                        <div className="col-md-8 col-lg-9">
                          <input
                            name="confirm_password"
                            type="password"
                            className="form-control"
                            id="confirm_password"
                            value={passwordData.confirm_password}
                            onChange={handlePasswordChange}
                            required
                            minLength={8}
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="text-center">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? 'Changing Password...' : 'Change Password'}
                        </button>
                      </div>
                    </form>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Profile

